import { createSlice, createSelector, PayloadAction } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { MW_5_15_0 } from '../../constants/migrationVersions';
import { runMigration } from '../../migrations';

// Type definitions
export interface Coordinates {
  latitude: number;
  longitude: number;
}

export interface LandingZone {
  external_id?: string;
  name?: string;
  center?: {
    coordinates?: [number, number];
  };
}

export interface AirAmbulanceRequest {
  topic?: string;
  body?: Record<string, unknown>;
  status?: { type?: string; timestamp?: string }[];
  trip_data?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface SelectedAirAmbulance extends AirAmbulanceRequest {
  airAmbulanceID: string;
}

interface AirAmbulanceRequests {
  calls: Record<string, AirAmbulanceRequest>;
  nonCalls: Record<string, AirAmbulanceRequest>;
}

export interface AirAmbulanceState {
  requests: AirAmbulanceRequests;
  removedAirAmbulances: Record<string, AirAmbulanceRequest>;
  selectedAirAmbulance: SelectedAirAmbulance | null;
  formPageToShow: string | null;
  hoveredAirAmbulance: SelectedAirAmbulance | null;
  temporaryLocation: Coordinates | null;
  tentativeETALocation: Coordinates | null;
  isNonCallSubmit: boolean;
  paneOpen: boolean;
  preselectedLandingZone: LandingZone | null;
}

// Root state interface for selectors
interface RootState {
  airAmbulance: AirAmbulanceState;
  emergencyCalls: {
    selectedCallerID: string | null;
  };
}

const initialState: AirAmbulanceState = {
  requests: { calls: {}, nonCalls: {} },
  removedAirAmbulances: { calls: {}, nonCalls: {} }, // removed rescues
  selectedAirAmbulance: null,
  formPageToShow: null,
  hoveredAirAmbulance: null,
  temporaryLocation: null, // hovered and selected address for TempLocationGL (should probably move to a new slice)
  tentativeETALocation: null, // the location to fetch ETA for
  isNonCallSubmit: false,
  paneOpen: false, // set whether the pane should be open or not
  preselectedLandingZone: null, // landing zone selected from map marker
};

// Selector to get existing request
export const selectExistingRequest = createSelector(
  [
    (state: RootState) => state.airAmbulance.requests,
    (state: RootState) => state.emergencyCalls.selectedCallerID,
    (state: RootState) => state.airAmbulance.selectedAirAmbulance,
  ],
  (
    requests,
    selectedCallerID,
    selectedAirAmbulance,
  ): AirAmbulanceRequest | null => {
    return (
      (selectedCallerID && requests?.calls?.[selectedCallerID]) ||
      (selectedAirAmbulance?.airAmbulanceID &&
        requests?.nonCalls?.[selectedAirAmbulance.airAmbulanceID]) ||
      null
    );
  },
);

// Selector to get tentative ETA location
export const selectTentativeETALocation = createSelector(
  [
    (state: RootState) => state.airAmbulance.tentativeETALocation,
    selectExistingRequest,
  ],
  (tentativeETALocation, existingRequest): Coordinates | null =>
    tentativeETALocation ||
    (existingRequest?.body?.location as Coordinates) ||
    null,
);

const airAmbulanceSlice = createSlice({
  name: 'airAmbulance',
  initialState,
  reducers: {
    setIsNonCallSubmit: (state, action: PayloadAction<boolean>) => {
      state.isNonCallSubmit = action.payload;
    },
    setPaneOpen: (state, action: PayloadAction<boolean>) => {
      state.paneOpen = action.payload;
    },
    setSelectedAirAmbulance: (state, action: PayloadAction<string>) => {
      const airAmbulanceID = action.payload;
      state.selectedAirAmbulance = {
        ...state.requests.nonCalls[airAmbulanceID],
        airAmbulanceID,
      };
    },
    removeAirAmbulanceRequest: (state, action: PayloadAction<string>) => {
      const airAmbulanceID = action.payload;
      const isCall = state.requests.calls[airAmbulanceID];
      if (isCall) {
        state.removedAirAmbulances[airAmbulanceID] =
          state.requests.calls[airAmbulanceID];
        delete state.requests.calls[airAmbulanceID];
      } else {
        state.removedAirAmbulances[airAmbulanceID] =
          state.requests.nonCalls[airAmbulanceID];
        delete state.requests.nonCalls[airAmbulanceID];
      }
    },
    resetSelectedAirAmbulance: state => {
      return {
        ...state,
        selectedAirAmbulance: null,
      };
    },
    setPreselectedLandingZone: (
      state,
      action: PayloadAction<LandingZone | null>,
    ) => {
      return {
        ...state,
        preselectedLandingZone: action.payload,
      };
    },
    resetPreselectedLandingZone: state => {
      return {
        ...state,
        preselectedLandingZone: null,
      };
    },
    setHoveredAirAmbulance: (
      state,
      action: PayloadAction<SelectedAirAmbulance | null>,
    ) => {
      return {
        ...state,
        hoveredAirAmbulance: action.payload,
      };
    },
    setTemporaryLocation: (
      state,
      action: PayloadAction<Coordinates | null>,
    ) => {
      return {
        ...state,
        temporaryLocation: action.payload,
      };
    },
    setTentativeETALocation: (
      state,
      action: PayloadAction<Coordinates | null>,
    ) => {
      return {
        ...state,
        tentativeETALocation: action.payload,
      };
    },
    resetHoveredAirAmbulance: state => {
      return {
        ...state,
        hoveredAirAmbulance: null,
      };
    },
    addAndUpdateAirAmbulanceRequest: (
      state,
      action: PayloadAction<{
        message: AirAmbulanceRequest;
        hasMostRecentLocation?: boolean;
      }>,
    ) => {
      const message = action.payload?.message;
      const hasMostRecentLocation = action.payload?.hasMostRecentLocation;
      // Check if it already exists in inactive
      const isCallPayload = message?.topic?.includes('tel');
      const airAmbulanceID = message?.topic?.split('//')[1];
      if (isCallPayload && airAmbulanceID) {
        state.requests = {
          ...state.requests,
          calls: { ...state.requests.calls, [airAmbulanceID]: message },
        };
        state.formPageToShow = 'air_ambulance_status';
      } else if (airAmbulanceID) {
        const airAmbulanceWithId = { ...message, airAmbulanceID };
        const shouldSelectAirAmbulance =
          (!hasMostRecentLocation && state.isNonCallSubmit) || state.paneOpen;

        return {
          ...state,
          selectedAirAmbulance: shouldSelectAirAmbulance
            ? airAmbulanceWithId
            : null,
          requests: {
            ...state.requests,
            nonCalls: {
              ...state.requests.nonCalls,
              [airAmbulanceID]: message,
            },
          },
          formPageToShow: 'air_ambulance_status', // if request is non-call we can safely assume it's from the action bar while in all view
          isNonCallSubmit: false, // reset the isNonCallSubmit to false after the request is submitted
        };
      }
    },
    removeAllNonCallEvents: state => {
      const newState = {
        ...state,
        requests: { ...state.requests, nonCalls: {} },
        selectedAirAmbulance: null,
        removedAirAmbulances: {
          ...state.removedAirAmbulances,
          nonCalls: {
            ...state.removedAirAmbulances.nonCalls,
            ...state.requests.nonCalls,
          },
        },
      };

      return newState;
    },
    /**
     * Updates the status of an air ambulance request for a given phone number.
     * @param {Object} state - The current Redux state.
     * @param {Object} action - The dispatched action containing the payload.
     * @param {string} action.payload.phone - The phone number associated with the request.
     * @param {string} action.payload.status - The new status to update for the request.
     */
    updateAirAmbulanceRequestStatus: (
      state,
      action: PayloadAction<{ phone: string; status: string }>,
    ) => {
      const { phone, status } = action.payload;
      const requestToUpdate = state.requests.calls[phone];
      if (requestToUpdate) {
        requestToUpdate.status = [{ type: status }];
      }
    },

    setFormPageToShow: (state, action: PayloadAction<string | null>) => {
      state.formPageToShow = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addCase('APP_INIT_START', state => {
      const migrationList = [MW_5_15_0];
      let newState = { ...state };
      migrationList.forEach(migration => {
        newState = runMigration(newState, 'airAmbulance', migration);
      });
      return newState;
    });
  },
});

export const {
  setPaneOpen,
  setIsNonCallSubmit,
  setSelectedAirAmbulance,
  resetSelectedAirAmbulance,
  setPreselectedLandingZone,
  resetPreselectedLandingZone,
  addAndUpdateAirAmbulanceRequest,
  updateAirAmbulanceRequestStatus,
  removeAirAmbulanceRequest,
  setFormPageToShow,
  removeAllNonCallEvents,
  setHoveredAirAmbulance,
  resetHoveredAirAmbulance,
  setTemporaryLocation,
  setTentativeETALocation,
} = airAmbulanceSlice.actions;

const persistConfig = {
  key: 'airAmbulance',
  storage,
  blacklist: ['temporaryLocation', 'tentativeETALocation'],
};

const persistedReducer = persistReducer(
  persistConfig,
  airAmbulanceSlice.reducer,
);

export default persistedReducer;
